import React, { useState, useEffect } from 'react';
import { useAuth, TenantPlan } from '../context/AuthContext';
import { superadminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Building2, Users, CheckCircle, Clock, AlertTriangle, XCircle, 
  RefreshCw, Play, Ban, Calendar, Eye, Loader2, Shield, BarChart3,
  Bot, Sparkles
} from 'lucide-react';

const planBadges = {
  [TenantPlan.ACTIVE]: { label: 'Activo', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  [TenantPlan.TRIAL]: { label: 'Trial', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  [TenantPlan.TRIAL_EXPIRED]: { label: 'Expirado', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700' },
  [TenantPlan.BLOCKED]: { label: 'Bloqueado', icon: XCircle, color: 'bg-red-100 text-red-700' },
};

const SuperadminDashboard = () => {
  const { isSuperadmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [extendDays, setExtendDays] = useState(7);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        superadminAPI.listTenants(),
        superadminAPI.getStats(),
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantDetails = async (tenantId) => {
    setDetailsLoading(true);
    try {
      const response = await superadminAPI.getTenant(tenantId);
      setTenantDetails(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      await superadminAPI.activateTenant(tenantId);
      toast.success('Tenant activado');
      loadData();
      if (tenantDetails?.tenant?.id === tenantId) {
        loadTenantDetails(tenantId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleBlock = async (tenantId, reason = 'Bloqueado por administrador') => {
    if (!window.confirm('¿Bloquear este tenant?')) return;
    try {
      await superadminAPI.blockTenant(tenantId, reason);
      toast.success('Tenant bloqueado');
      loadData();
      if (tenantDetails?.tenant?.id === tenantId) {
        loadTenantDetails(tenantId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleExtendTrial = async (tenantId) => {
    try {
      await superadminAPI.extendTrial(tenantId, extendDays);
      toast.success(`Trial extendido ${extendDays} días`);
      loadData();
      if (tenantDetails?.tenant?.id === tenantId) {
        loadTenantDetails(tenantId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleReset = async (tenantId) => {
    if (!window.confirm('¿Resetear todos los datos del tenant demo?')) return;
    try {
      await superadminAPI.resetTenant(tenantId);
      toast.success('Tenant demo reseteado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleToggleMandoIA = async (tenantId, currentStatus, tenantName) => {
    const newStatus = !currentStatus;
    try {
      await superadminAPI.toggleMandoIA(tenantId, newStatus);
      toast.success(`MANDO IA ${newStatus ? 'activado' : 'desactivado'} para ${tenantName}`);
      loadData();
      if (tenantDetails?.tenant?.id === tenantId) {
        loadTenantDetails(tenantId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar MANDO IA');
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Acceso Denegado</h2>
            <p className="text-slate-500">
              Solo los superadmins pueden acceder a esta sección.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-6" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-slate-900">Superadmin Dashboard</h1>
        </div>
        <p className="text-slate-600">Gestión global de MANDO · by Leapifyes</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 text-center">
              <Building2 className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.tenants.total}</p>
              <p className="text-xs text-slate-500">Total Tenants</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{stats.tenants.active}</p>
              <p className="text-xs text-slate-500">Activos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-600">{stats.tenants.trial}</p>
              <p className="text-xs text-slate-500">En Trial</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-600">{stats.tenants.expired}</p>
              <p className="text-xs text-slate-500">Expirados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-600">{stats.users.total}</p>
              <p className="text-xs text-slate-500">Usuarios Totales</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenants List */}
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Tenants</CardTitle>
              <Button variant="outline" size="sm" onClick={loadData} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-slate-500">
                    <th className="pb-3 font-medium">Empresa</th>
                    <th className="pb-3 font-medium">Estado</th>
                    <th className="pb-3 font-medium">MANDO IA</th>
                    <th className="pb-3 font-medium">Usuarios</th>
                    <th className="pb-3 font-medium">Trial</th>
                    <th className="pb-3 font-medium">Creado</th>
                    <th className="pb-3 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tenants.map((tenant) => {
                    const planInfo = planBadges[tenant.plan] || planBadges[TenantPlan.TRIAL];
                    const trialDays = tenant.trial_status?.days_remaining;
                    const hasAI = tenant.mando_ia_enabled;
                    
                    return (
                      <tr key={tenant.id} className="hover:bg-slate-50" data-testid={`tenant-row-${tenant.id}`}>
                        <td className="py-3">
                          <div>
                            <p className="font-medium text-slate-900">{tenant.name}</p>
                            <p className="text-xs text-slate-500">{tenant.email}</p>
                            {tenant.is_demo && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">DEMO</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${planInfo.color}`}>
                            <planInfo.icon className="w-3 h-3" />
                            {planInfo.label}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={hasAI}
                              onCheckedChange={() => handleToggleMandoIA(tenant.id, hasAI, tenant.name)}
                              data-testid={`mando-ia-toggle-${tenant.id}`}
                            />
                            {hasAI && (
                              <div className="flex items-center gap-1 text-xs text-purple-600">
                                <Bot className="w-3 h-3" />
                                <Sparkles className="w-3 h-3" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-sm text-slate-600">{tenant.user_count}</td>
                        <td className="py-3 text-sm text-slate-600">
                          {trialDays !== null && trialDays !== undefined && (
                            <span className={trialDays > 0 ? 'text-blue-600' : 'text-amber-600'}>
                              {trialDays > 0 ? `${trialDays} días` : 'Expirado'}
                            </span>
                          )}
                          {tenant.plan === TenantPlan.ACTIVE && '-'}
                        </td>
                        <td className="py-3 text-sm text-slate-500">
                          {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTenant(tenant);
                                loadTenantDetails(tenant.id);
                              }}
                              className="gap-1"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {tenant.plan !== TenantPlan.ACTIVE && !tenant.is_demo && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleActivate(tenant.id)}
                                className="gap-1 text-green-600 hover:text-green-700"
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {tenant.plan !== TenantPlan.BLOCKED && !tenant.is_demo && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleBlock(tenant.id)}
                                className="gap-1 text-red-600 hover:text-red-700"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {tenant.is_demo && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReset(tenant.id)}
                                className="gap-1 text-amber-600 hover:text-amber-700"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Details Dialog */}
      <Dialog open={!!selectedTenant} onOpenChange={() => setSelectedTenant(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="tenant-details-dialog">
          <DialogHeader>
            <DialogTitle>{selectedTenant?.name}</DialogTitle>
            <DialogDescription>
              Detalles del tenant (solo metadatos, no datos operativos)
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : tenantDetails ? (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-5 gap-3 text-center">
                {Object.entries(tenantDetails.stats || {}).map(([key, value]) => (
                  <div key={key} className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-lg font-bold text-slate-900">{value}</p>
                    <p className="text-xs text-slate-500 capitalize">{key}</p>
                  </div>
                ))}
              </div>

              {/* Trial Info */}
              {tenantDetails.trial_status && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Estado del Trial</p>
                  <p className="text-sm text-slate-600">
                    Estado: <strong>{tenantDetails.trial_status.status}</strong>
                  </p>
                  {tenantDetails.trial_status.days_remaining !== null && (
                    <p className="text-sm text-slate-600">
                      Días restantes: <strong>{tenantDetails.trial_status.days_remaining}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* MANDO IA Section */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 flex items-center gap-2">
                        MANDO IA
                        <Sparkles className="w-4 h-4 text-yellow-500" />
                      </p>
                      <p className="text-xs text-slate-500">Asistente inteligente premium</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium ${tenantDetails.tenant?.mando_ia_enabled ? 'text-green-600' : 'text-slate-400'}`}>
                      {tenantDetails.tenant?.mando_ia_enabled ? 'Activado' : 'Desactivado'}
                    </span>
                    <Switch
                      checked={tenantDetails.tenant?.mando_ia_enabled || false}
                      onCheckedChange={() => handleToggleMandoIA(
                        tenantDetails.tenant?.id, 
                        tenantDetails.tenant?.mando_ia_enabled,
                        tenantDetails.tenant?.name
                      )}
                    />
                  </div>
                </div>
                {tenantDetails.tenant?.mando_ia_activated_at && (
                  <p className="text-xs text-slate-500 mt-2">
                    Activado el: {new Date(tenantDetails.tenant.mando_ia_activated_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Users */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Usuarios ({tenantDetails.users?.length || 0})
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {tenantDetails.users?.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{u.nombre}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </div>
                      <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">
                        {u.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              {!selectedTenant?.is_demo && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={extendDays}
                      onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                      className="w-20"
                      min={1}
                      max={30}
                    />
                    <Button
                      variant="outline"
                      onClick={() => handleExtendTrial(selectedTenant.id)}
                      className="gap-2"
                    >
                      <Calendar className="w-4 h-4" />
                      Extender Trial
                    </Button>
                  </div>
                  
                  <div className="flex gap-2">
                    {selectedTenant?.plan !== TenantPlan.ACTIVE && (
                      <Button
                        onClick={() => handleActivate(selectedTenant.id)}
                        className="gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <Play className="w-4 h-4" />
                        Activar
                      </Button>
                    )}
                    {selectedTenant?.plan !== TenantPlan.BLOCKED && (
                      <Button
                        variant="destructive"
                        onClick={() => handleBlock(selectedTenant.id)}
                        className="gap-2"
                      >
                        <Ban className="w-4 h-4" />
                        Bloquear
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="max-w-7xl mx-auto mt-8 text-center text-sm text-slate-500">
        MANDO · by Leapifyes - Panel de Administración
      </div>
    </div>
  );
};

export default SuperadminDashboard;
